/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The lab dialog's chart, inline in an in-depth pick's block (Guillaume, 2026-09-15: comparing
 * the section block with the dialog, the dialog is what he wants on the page). Same panel, same
 * feed as `LabCompareDialog`: this pick's curves in blue, the reader's declared pair overlaid in
 * orange, the four tabs, the band callout, the blurb and the band chips — so the two surfaces
 * cannot drift, and anything said here is said there.
 *
 * Weight is the reason it is its own file and its own chunk: `LabChartPanel` is ~1k lines, and a
 * guide has nine picks. It is imported dynamically, mounted only once the block is in view, and
 * reads curves through `curves.ts`'s per-page cache, which the nine panels share (the roster
 * draws no curves since 2026-09-16, so the first panel in view is what fills it).
 */
import dynamic from 'next/dynamic'
import { useMemo, useState } from 'react'
import { ResponsiveContext } from 'shared-components/contexts/ResponsiveContext'
import styles from './PickLabPanel.module.css'
import BandChips from '../BandChips'
import {
  labTabs,
  shownTab,
  type BandHighlight,
  type LabTab,
  type PanelOverlay,
} from 'components/product/LabChartPanel'
import {
  GEOM,
  GEOM_COMPACT,
  tabMeasure,
  type ChartGeometry,
  type LabChartPaths,
  type LabMeasure,
  type LabTabKey,
} from 'lib/dealhunt/audioChart'
import type { OwnerTrack } from 'lib/owner-notes/analytics'
import { THIS_COLOR, YOURS_COLOR, useCurves } from 'lib/owner-notes/curves'
import type { DeclaredProduct } from 'lib/owner-notes/declared'
import {
  bandHeadline,
  bandSpread,
  bandVerdicts,
  type BandVerdict,
  type CurveKind,
} from 'lib/owner-notes/deltas'
import { flatSlugFromHref, useMdaqs } from 'lib/owner-notes/mdaqs'

// ssr:false as well as dynamic: the panel measures and draws, and the article's HTML is
// edge-cached for every visitor (#51). Since 2026-09-16 the panel also draws the pick ALONE for a
// reader who has declared nothing (`declared: null`) — still client-only, still in-view-only.
const LabChartPanel = dynamic(() => import('components/product/LabChartPanel'), { ssr: false })

/** The overlay's key, and what the hover readout subtracts from the pick's curve. */
const YOURS_KEY = 'yours'

const KIND_OF: Record<LabMeasure, CurveKind> = {
  frequency: 'frequency',
  isolation: 'attenuation',
  anc: 'attenuation',
}

interface PickLabPanelProps {
  productName: string
  /** The pick's PDP href — the MDAQS lookup and nothing else. */
  pdpHref: string | null
  /** The pick's curves, already in hand (the block mounts this only once they are). */
  curves: LabChartPaths
  /** Null: nobody has declared — the pick's curves alone, no overlay, no band chips, no hover
   *  difference. The tabs, the blurb and the MDAQS scores are the pick's and stay. */
  declared: DeclaredProduct | null
  trackOwner: OwnerTrack
  /** Unique per pick: two panels on one page must not share element ids. */
  idPrefix: string
  /** Where the panel sits, for `lab_open`: the roster row or the in-depth block (the default). */
  location?: 'roster' | 'pick' | 'grid'
}

const PickLabPanel: React.VFC<PickLabPanelProps> = ({
  productName,
  pdpHref,
  curves,
  declared,
  trackOwner,
  idPrefix,
  location = 'pick',
}) => {
  const responsive = ResponsiveContext.useOptionalContainer()
  const geom: ChartGeometry = responsive?.ready === true && responsive.md ? GEOM : GEOM_COMPACT

  const myId = declared?.clearbuyProductId ?? null
  // The first panel to mount fetches the declared product's curves; the module cache in
  // `curves.ts` hands them to the other eight. Nothing upstream primes it any more.
  const myCurves = useCurves(myId, myId !== null)
  const theirMdaqs = useMdaqs(flatSlugFromHref(pdpHref), true)
  const myMdaqs = useMdaqs(declared ? flatSlugFromHref(declared.href) : null, declared !== null)

  const [chosen, setChosen] = useState<string | null>(null)
  // The drawer keeps this panel mounted across picks: a tab the reader chose on one pick may not
  // exist on the next, so the chips read the tab the panel actually shows.
  const [requested, setActive] = useState<LabTabKey>('freq')
  const active = shownTab(
    labTabs(curves, theirMdaqs === undefined || (theirMdaqs?.scores.length ?? 0) > 0),
    requested
  )
  const measure: LabMeasure | null = active === 'mdaqs' ? null : tabMeasure(active, curves)

  const verdicts = useMemo<BandVerdict[]>(() => {
    const mine = measure ? myCurves?.[measure] : null
    const theirs = measure ? curves[measure] : null
    if (!mine || !theirs) {
      return []
    }
    return bandVerdicts(mine.points, theirs.points)
  }, [measure, curves, myCurves])
  const selected = verdicts.find(v => v.band.name === chosen) ?? null
  const kind: CurveKind | null = measure ? KIND_OF[measure] : null
  const highlight: BandHighlight | null =
    selected && measure && kind
      ? {
          measure,
          lo: selected.band.lo,
          hi: selected.band.hi,
          text: bandHeadline(selected),
          detail: bandSpread(selected, kind),
          against: YOURS_KEY,
        }
      : null

  const overlays: PanelOverlay[] = declared
    ? [
        {
          key: YOURS_KEY,
          name: `${declared.name} · yours`,
          color: YOURS_COLOR,
          curves: myId === null ? null : myCurves,
          mdaqs: myMdaqs?.overlay ?? null,
        },
      ]
    : []

  return (
    <div className={styles.root}>
      <LabChartPanel
        className={styles.panel}
        idPrefix={idPrefix}
        productName={productName}
        primaryColor={THIS_COLOR}
        curves={curves}
        mdaqs={theirMdaqs?.scores ?? []}
        mdaqsPending={theirMdaqs === undefined}
        overlays={overlays}
        active={active}
        onTabClick={(tab: LabTab) => {
          setActive(tab.key)
          setChosen(null)
          trackOwner('lab_open', { lab_tab: tab.key, location })
        }}
        geom={geom}
        tables={false}
        highlight={highlight}
        hoverDiff={declared ? YOURS_KEY : undefined}
      />
      {verdicts.length > 0 && kind && (
        <BandChips
          verdicts={verdicts}
          kind={kind}
          selected={selected ?? null}
          onSelect={setChosen}
        />
      )}
    </div>
  )
}

export default PickLabPanel
